import styled from 'styled-components';
import { Show } from '@elfsight/lib-utils';
import {
  createReadableClass,
  getColorWithAlpha,
  getInvertedColor
} from '@elfsight/widget-utils';
import type { AnimatedContainerProps } from './animation';
import { AnimatedContainer } from './animation';

export type CounterStyle =
  | 'clean'
  | 'separated'
  | 'combined'
  | 'gradient'
  | 'outline';

export type CounterProps = {
  children: number;
  counterStyle: CounterStyle;
  animation: AnimatedContainerProps['animation'];
  color: string;
  backgroundColor: string;
  maxLength?: number;
  className?: string;
};

export function Counter({
  children,
  className,
  counterStyle,
  color,
  backgroundColor,
  animation,
  maxLength = 2
}: CounterProps) {
  const stringChildren = String(children);
  const value = stringChildren.padStart(maxLength, '0');
  const splitChildren = value.split('');

  return (
    <>
      <Show when={counterStyle === 'clean'}>
        <Clean
          className={className}
          animation={animation === 'flip' ? 'none' : animation}
          accentColor={backgroundColor}
          _color={color}
        >
          {value}
        </Clean>
      </Show>

      <Show when={counterStyle === 'separated'}>
        <Separated className={className}>
          {splitChildren.map((value, index) => (
            <SeparatedValue
              key={index}
              _color={color}
              _backgroundColor={backgroundColor}
              animation={animation}
              accentColor={backgroundColor}
            >
              {value}
            </SeparatedValue>
          ))}
        </Separated>
      </Show>

      <Show when={counterStyle === 'combined'}>
        <Combined
          className={className}
          animation={animation}
          accentColor={backgroundColor}
          _color={color}
          _backgroundColor={backgroundColor}
        >
          {value}
        </Combined>
      </Show>

      <Show when={counterStyle === 'gradient'}>
        <Gradient>
          {splitChildren.map((value, index) => (
            <GradientValue
              key={index}
              className={className}
              animation={animation}
              accentColor={backgroundColor}
              _color={color}
              _backgroundColor={backgroundColor}
            >
              {value}
            </GradientValue>
          ))}
        </Gradient>
      </Show>

      <Show when={counterStyle === 'outline'}>
        <Outline
          className={className}
          animation={animation}
          accentColor={getInvertedColor(color)}
          _color={color}
        >
          {value}
        </Outline>
      </Show>
    </>
  );
}

const Base = styled(AnimatedContainer).attrs<{
  _color: string;
}>({
  className: createReadableClass({ component: 'counter', element: 'base' })
})`
  box-sizing: content-box !important;
  position: relative;
  text-align: center;
  align-self: stretch;
  font-weight: 700;
  line-height: 1.3125;
  padding: 0 0.1875em;
  border-radius: 0.125em;
  overflow: hidden;
  color: ${({ _color }) => _color};
`;

const Clean = styled(Base).attrs({
  className: createReadableClass({ component: 'counter', element: 'clean' })
})``;

const Separated = styled.div.attrs({
  className: createReadableClass({ component: 'counter', element: 'separated' })
})`
  display: flex;
  gap: 0.0625em;
`;

const SeparatedValue = styled(Base).attrs<{
  _backgroundColor: string;
}>({
  className: createReadableClass({
    component: 'counter',
    element: 'separated-value'
  })
})`
  width: 0.75em;
  background-color: ${({ _backgroundColor }) => _backgroundColor};
`;

const Combined = styled(Base).attrs<{
  _backgroundColor: string;
}>({
  className: createReadableClass({ component: 'counter', element: 'combined' })
})`
  background-color: ${({ _backgroundColor }) => _backgroundColor};
`;

const Gradient = styled.div.attrs({
  className: createReadableClass({ component: 'counter', element: 'gradient' })
})`
  display: flex;
  gap: 0.0625em;
`;

const GradientValue = styled(Base).attrs<{
  _backgroundColor: string;
}>({
  className: createReadableClass({
    component: 'counter',
    element: 'gradient-value'
  })
})`
  width: 0.75em;
  overflow: hidden;
  background-color: ${({ _backgroundColor }) => _backgroundColor};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: calc(50% - 0.03125em);
    border-top-left-radius: inherit;
    border-top-right-radius: inherit;
    background: linear-gradient(
      to top,
      rgba(0, 0, 0, 0) 0%,
      ${({ _backgroundColor }) => getColorWithAlpha(_backgroundColor, 0.3)} 100%
    );
  }

  &::after {
    content: '';
    position: absolute;
    top: calc(50% + 0.03125em);
    left: 0;
    width: 100%;
    height: calc(50% - 0.03125em);
    border-bottom-left-radius: inherit;
    border-bottom-right-radius: inherit;
    background: linear-gradient(
      to top,
      rgba(0, 0, 0, 0) 0%,
      ${({ _backgroundColor }) => getColorWithAlpha(_backgroundColor, 0.3)} 100%
    );
  }
`;

const Outline = styled(Base).attrs({
  className: createReadableClass({ component: 'counter', element: 'outline' })
})`
  box-shadow: ${({ _color }) =>
    `inset 0 0 0 0.03125em ${getColorWithAlpha(_color, 0.3)}`};
`;
